import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * Quick view morphs only `variant-picker` on option change; `product-oos-similar-products`
 * is rendered by Liquid on the fetched product HTML but was never synced into the modal.
 * @param {Document} html - Parsed response from the same fetch as `variant-picker`.
 * @param {HTMLElement} picker - The variant-picker instance (must be inside the QV dialog).
 */
function syncQuickViewProductOosBlock(html, picker) {
  if (!picker.closest('quick-view-dialog')) return;
  const modalContent = document.getElementById('quick-view-modal-content');
  if (!modalContent) return;

  const sourceOos = html.querySelector('.product-oos-similar-products');
  const targetOos = modalContent.querySelector('.product-oos-similar-products');

  if (sourceOos && targetOos) {
    morph(targetOos, sourceOos);
    return;
  }
  if (sourceOos && !targetOos) {
    const variantHost = modalContent.querySelector('variant-picker')?.closest('.group-block');
    if (!variantHost?.parentElement) return;
    variantHost.parentElement.insertBefore(
      /** @type {Node} */ (sourceOos.cloneNode(true)),
      variantHost
    );
    return;
  }
  if (!sourceOos && targetOos) {
    targetOos.remove();
  }
}

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [Refs = {}]
 *
 * @extends Component<Refs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('change', this.variantChanged.bind(this));
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;
    this.updateSelectedOption(event.target);

    // Get the actual element with data attributes (either the input or the selected option)
    let targetElement = event.target;
    let variantId = null;
    let variantSku = null;

    if (event.target instanceof HTMLSelectElement) {
      const selectedOption = event.target.options[event.target.selectedIndex];
      if (selectedOption) {
        targetElement = selectedOption;
        variantId = selectedOption.dataset.variantId || null;
        variantSku = selectedOption.dataset.variantSku || null;
      }
    } else if (event.target instanceof HTMLInputElement && event.target.type === 'radio') {
      variantId = event.target.dataset.variantId || null;
      variantSku = event.target.dataset.variantSku || null;
    }

    this.dispatchEvent(new VariantSelectedEvent({ id: targetElement.dataset.optionValueId ?? '' }));

    const templateName =
      (typeof Theme !== 'undefined' && Theme?.template?.name) ||
      document.querySelector('main')?.dataset.template ||
      '';
    const isProductTemplate = /^product(?:[\.\-]|$)/.test(templateName);
    const isStandaloneQuickViewPage =
      document.body.classList.contains('template-quick-view') ||
      document.body.getAttribute('data-template') === 'product-quick-view';
    const isInQuickViewModal = !!event.target.closest('quick-view-dialog');
    const isOnProductPage =
      isProductTemplate &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog') &&
      !isInQuickViewModal &&
      !isStandaloneQuickViewPage;

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = targetElement.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;

    this.fetchUpdatedSection(
      this.buildRequestUrl(targetElement),
      loadsNewProduct,
      variantSku,
      isOnProductPage
    );

    const url = new URL(window.location.href);

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      history.replaceState({}, '', url.toString());
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find(
        (option) => option.value === newValue
      );

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl =
      selectedOption.dataset.connectedProductUrl ||
      this.#pendingRequestUrl ||
      this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of quick-add-component or swatches-variant-picker-component, we need to append section_id=section-rendering-product-card to the URL
    if (this.closest('quick-add-component') || this.closest('swatches-variant-picker-component')) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=section-rendering-product-card&${params.join('&')}`;
    }

    const useQuickViewProductRequest =
      !!this.closest('quick-view-dialog') ||
      document.body.classList.contains('template-quick-view');
    if (useQuickViewProductRequest) {
      params.unshift('view=quick-view');
    } else {
      const alternateTemplateSuffix = this.getAlternateProductTemplateSuffix();
      if (alternateTemplateSuffix) {
        params.unshift(`view=${encodeURIComponent(alternateTemplateSuffix)}`);
      }
    }

    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Returns the alternate product template suffix (e.g. "ab-hide-sub-price" for
   * templates/product.ab-hide-sub-price.json) so variant fetches render the same template
   * the page was loaded with. Returns null on the default product template.
   * @returns {string | null}
   */
  getAlternateProductTemplateSuffix() {
    const templateName =
      (typeof Theme !== 'undefined' && Theme?.template?.name) ||
      document.querySelector('main')?.dataset.template ||
      '';
    const [templateBase, ...suffixParts] = templateName.split('.');
    if (templateBase !== 'product') return null;

    const suffixFromTemplate = suffixParts.join('.');
    const suffixFromUrl = new URLSearchParams(window.location.search).get('view');
    const suffix = suffixFromTemplate || suffixFromUrl;
    if (!suffix || suffix === 'quick-view') return null;

    return suffix;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {boolean} shouldMorphMain - If the entire main content should be morphed. By default, only the variant picker is morphed.
   * @param {string | null} variantSku - The variant SKU.
   * @param {boolean} canMorphMain - True on the product page proper (not cards/quick-add/quick-view), where a full main morph is allowed as an escalation.
   */
  fetchUpdatedSection(
    requestUrl,
    shouldMorphMain = false,
    variantSku = null,
    canMorphMain = false
  ) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    const dialog = this.closest('dialog');
    if (dialog) dialog.setAttribute('aria-busy', 'true');

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(
          `variant-picker script[type="application/json"]`
        )?.textContent;
        if (!textContent) {
          if (dialog) dialog.removeAttribute('aria-busy');
          return;
        }

        // COMB-03: transitions into or out of the no-variant (combination unavailable) state
        // change blocks OUTSIDE the picker — the message appears/disappears and the buy form
        // is dropped from the render entirely, so it cannot restore itself via events.
        // Escalate those transitions to a full re-render.
        const currentVariantJson = this.querySelector(
          'script[type="application/json"]'
        )?.textContent?.trim();
        const crossesNoVariantState =
          currentVariantJson === 'null' || textContent.trim() === 'null';

        if (shouldMorphMain || (canMorphMain && crossesNoVariantState)) {
          this.updateMain(html);
          if (dialog) dialog.removeAttribute('aria-busy');
        } else {
          // Snapshot before morph: updateVariantPicker replaces the picker DOM and can reset
          // selection to the server default, which would drop option_values on the follow-up QV fetch.
          const optionValuesForCrossProduct = [...this.selectedOptionsValues];
          const newProduct = this.updateVariantPicker(html);

          // COMB-02: Cross-product morph for Combined Listings inside Quick View dialog.
          // When variant switch targets a different child product, re-fetch and morph the entire
          // modal content container so media gallery + details update — not just the variant picker.
          // Gate on quick-view-dialog only: this.closest('dialog') also matches quick-add's <dialog>.
          if (newProduct && this.closest('quick-view-dialog')) {
            const modalContent = document.getElementById('quick-view-modal-content');
            const productPath = new URL(newProduct.url, window.location.origin).pathname;
            const crossProductQs = new URLSearchParams();
            crossProductQs.set('view', 'quick-view');
            if (optionValuesForCrossProduct.length > 0) {
              crossProductQs.set('option_values', optionValuesForCrossProduct.join(','));
            }
            const connectedQvUrl = `${productPath}?${crossProductQs.toString()}`;

            fetch(connectedQvUrl, { signal: this.#abortController.signal })
              .then((res) => res.text())
              .then((crossProductText) => {
                const crossDoc = new DOMParser().parseFromString(crossProductText, 'text/html');
                if (modalContent) morph(modalContent, crossDoc.body);

                const crossTextContent = crossDoc.querySelector(
                  'variant-picker script[type="application/json"]'
                )?.textContent;
                // After morph(modalContent, …), this variant-picker instance is detached from the DOM.
                // Bubbling VariantUpdateEvent from `this` never reaches dialog-level listeners (MediaGallery,
                // product-form, etc.). Dispatch from the dialog (or modal container) and resolve sourceId
                // from the live morphed picker.
                if (crossTextContent) {
                  const livePicker = modalContent?.querySelector('variant-picker');
                  const liveSelected = livePicker?.querySelector(
                    'select option[selected], fieldset input:checked'
                  );
                  let sourceId;
                  if (
                    liveSelected instanceof HTMLInputElement ||
                    liveSelected instanceof HTMLOptionElement
                  ) {
                    sourceId = liveSelected.dataset.optionValueId;
                  }
                  if (!sourceId) {
                    try {
                      sourceId = this.selectedOptionId;
                    } catch {
                      // Detached / incomplete picker DOM after morph
                    }
                  }
                  const dispatchTarget = dialog ?? modalContent;
                  if (sourceId && dispatchTarget) {
                    dispatchTarget.dispatchEvent(
                      new VariantUpdateEvent(JSON.parse(crossTextContent), sourceId, {
                        html: crossDoc,
                        productId: newProduct.id,
                        newProduct,
                      })
                    );
                  }
                }
                if (dialog) dialog.removeAttribute('aria-busy');
              })
              .catch((err) => {
                if (err.name !== 'AbortError') {
                  console.error('[variant-picker] cross-product QV fetch failed', err);
                }
                if (dialog) dialog.removeAttribute('aria-busy');
              });
            return;
          }

          // COMB-03 (quick view): same-product transition into/out of the no-variant state.
          // The response was fetched with view=quick-view, so its body is the full modal
          // content — morph it whole to restore/remove the buy form and message together.
          if (crossesNoVariantState && this.closest('quick-view-dialog')) {
            const modalContent = document.getElementById('quick-view-modal-content');
            if (modalContent) morph(modalContent, html.body);

            const livePicker = modalContent?.querySelector('variant-picker');
            const liveSelected = livePicker?.querySelector(
              'select option[selected], fieldset input:checked'
            );
            let sourceId;
            if (
              liveSelected instanceof HTMLInputElement ||
              liveSelected instanceof HTMLOptionElement
            ) {
              sourceId = liveSelected.dataset.optionValueId;
            }
            const dispatchTarget = dialog ?? modalContent;
            if (sourceId && dispatchTarget) {
              dispatchTarget.dispatchEvent(
                new VariantUpdateEvent(JSON.parse(textContent), sourceId, {
                  html,
                  productId: this.dataset.productId ?? '',
                  newProduct: undefined,
                })
              );
            }
            if (dialog) dialog.removeAttribute('aria-busy');
            return;
          }

          syncQuickViewProductOosBlock(
            html,
            /** @type {HTMLElement} */ (/** @type {unknown} */ (this))
          );

          // We grab the variant object from the response and dispatch an event with it.
          if (this.selectedOptionId) {
            this.dispatchEvent(
              new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
              })
            );
          }
          if (dialog) dialog.removeAttribute('aria-busy');
        }

        const currentPrice = document.getElementById('current-price')?.textContent?.trim();

        requestAnimationFrame(() => {
          if (
            currentPrice &&
            window.zrl_mi &&
            typeof window.zrl_mi.replace_product_page_potential === 'function'
          ) {
            const ogPriceMeta = document.getElementById('og-price-amount');
            if (ogPriceMeta) {
              ogPriceMeta.setAttribute('content', currentPrice);
              window.zrl_mi.replace_product_page_potential();
            }
          }

          if (variantSku && typeof window.TurnToCmd === 'function') {
            window.TurnToCmd('set', { sku: variantSku });
            window.TurnToCmd('initialize', 'reviews-summary');
            window.TurnToCmd('initialize', 'reviews-list');
          }
        });
      })
      .catch((error) => {
        if (dialog) dialog.removeAttribute('aria-busy');
        if (error.name === 'AbortError') {
          console.log('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource);

    return newProduct;
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }
    document.getElementById('thumb-1')?.click();
    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (
      !(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)
    ) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(
      this.querySelectorAll('select option[selected], fieldset input:checked')
    );

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
